import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { fetchTopicContent, submitQuiz, fetchQuizAttempts } from "../../../services/topicContentService";
import "./TopicContentRenderer.css";

const BASE_ROOT = process.env.REACT_APP_BASE_URL_ROOT || "";
const toSrc = (url) => (!url ? "" : url.startsWith("http") ? url : `${BASE_ROOT}${url}`);

const getEmbedUrl = (url) => {
  if (!url) return null;
  const ytMatch = url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([-\w]{11})/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?([0-9]+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return null;
};

const DIFF_COLOR = { BEGINNER: "#4caf50", INTERMEDIATE: "#ff9800", ADVANCED: "#e91e63" };
const DIFF_BG    = { BEGINNER: "#f1faf1", INTERMEDIATE: "#fff8f0", ADVANCED: "#fff0f4" };
const PRACTICE_LABEL = {
  FILL_BLANK: "Fill in the Blank", REORDER_SENTENCE: "Reorder Sentence",
  SPEAKING: "Speaking", WRITING: "Writing", MATCHING: "Matching",
};

// ── Protected video player — fetches blob with JWT ────────────────────────────
const ProtectedVideo = ({ videoUrl, videoName }) => {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const blobRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const fullUrl = toSrc(videoUrl);
    setLoading(true);
    fetch(fullUrl, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((res) => {
        if (!res.ok) throw new Error("fetch failed");
        return res.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        blobRef.current = url;
        setSrc(url);
        setLoading(false);
      })
      .catch(() => { setError(true); setLoading(false); });
    return () => { if (blobRef.current) URL.revokeObjectURL(blobRef.current); };
  }, [videoUrl]);

  if (loading) return <div className="tcr-video-loading">Loading video…</div>;
  if (error)   return <div className="tcr-video-loading">Video unavailable</div>;
  return <video src={src} controls className="tcr-video-frame" style={{ background: "#000" }} />;
};

// ── Content Block ─────────────────────────────────────────────────────────────
export const ContentBlock = ({ data }) => {
  const navigate = useNavigate();
  const isProtected = (url) => url?.includes("/api/client/");

  return (
    <div className="tcr-content-card">
      {/* Difficulty + Category badges */}
      {(data.difficulty || data.category) && (
        <div className="tcr-content-meta">
          {data.difficulty && (
            <span className="tcr-difficulty-badge" style={{
              background: DIFF_BG[data.difficulty?.toUpperCase()] || "#f5f5f5",
              color: DIFF_COLOR[data.difficulty?.toUpperCase()] || "#666",
              border: `1px solid ${DIFF_COLOR[data.difficulty?.toUpperCase()] || "#ccc"}44`,
            }}>
              {data.difficulty}
            </span>
          )}
          {data.category && <span className="tcr-category-label">{data.category.name ?? data.category}</span>}
        </div>
      )}

      <div className="tcr-html-body" dangerouslySetInnerHTML={{ __html: data.body }} />

      {/* Uploaded videos */}
      {data.videos?.length > 0 && (
        <div className="tcr-videos">
          {data.videos.map((v) => (
            <div key={v.id ?? v.mediaVideoId} className="tcr-video-item">
              {v.videoName && <div className="tcr-video-label">🎬 {v.videoName}</div>}
              {v.videoUrl ? (
                isProtected(v.videoUrl)
                  ? <ProtectedVideo videoUrl={v.videoUrl} videoName={v.videoName} />
                  : <video src={toSrc(v.videoUrl)} controls className="tcr-video-frame" style={{ background: "#000" }} />
              ) : (
                <div className="tcr-video-loading">Video not available</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* YouTube / Vimeo embed */}
      {data.youtubeUrl && getEmbedUrl(data.youtubeUrl) && (
        <div className="tcr-videos">
          <div className="tcr-video-item">
            <iframe
              className="tcr-video-frame"
              src={getEmbedUrl(data.youtubeUrl)}
              title={data.title}
              allowFullScreen
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
            />
          </div>
        </div>
      )}

      {/* Images */}
      {data.images?.length > 0 && (
        <div className="tcr-images">
          {data.images.map((img) => (
            <div key={img.id} className="tcr-image-item">
              <img src={toSrc(img.imageUrl || img.url)} alt={img.caption || ""} />
              {img.caption && <p className="tcr-image-caption">{img.caption}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Vocabulary chips */}
      {data.vocabularies?.length > 0 && (
        <div className="tcr-chips-section">
          <div className="tcr-chips-title">📚 Vocabulary</div>
          <div className="tcr-chip-list">
            {data.vocabularies.map((v) => (
              <div key={v.id} className="tcr-chip" onClick={() => navigate(`/study/vocabulary/${v.id}`)}>
                <span className="tcr-chip-label">{v.word}</span>
                {v.partOfSpeech && <span className="tcr-chip-sub">{v.partOfSpeech}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Phrase chips */}
      {data.phrases?.length > 0 && (
        <div className="tcr-chips-section">
          <div className="tcr-chips-title">💡 Phrases</div>
          <div className="tcr-chip-list">
            {data.phrases.map((p) => (
              <div key={p.id} className="tcr-chip tcr-chip-phrase" onClick={() => navigate(`/study/phrases/${p.id}`)}>
                <span className="tcr-chip-label">{p.title}</span>
                {p.meaning && <span className="tcr-chip-sub">{p.meaning}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Practice Block ────────────────────────────────────────────────────────────
const PracticeBlock = ({ data }) => {
  const [revealed, setRevealed] = useState({});
  const [hints, setHints] = useState({});
  return (
    <div className="tcr-practice-card">
      <div className="tcr-practice-header">
        <div className="tcr-practice-title">📝 {data.title}</div>
        {data.practiceType && (
          <span className="tcr-practice-type-badge">
            {PRACTICE_LABEL[data.practiceType] || data.practiceType}
          </span>
        )}
      </div>
      {data.instructions && <div className="tcr-practice-instructions">{data.instructions}</div>}
      <div className="tcr-practice-items">
        {data.items?.map((item) => (
          <div key={item.id} className="tcr-practice-item">
            <div className="tcr-practice-question">{item.question}</div>
            <div>
              {item.hint && (
                <button className="tcr-hint-btn" onClick={() => setHints((h) => ({ ...h, [item.id]: !h[item.id] }))}>
                  {hints[item.id] ? "Hide Hint" : "Show Hint"}
                </button>
              )}
              <button className="tcr-reveal-btn" onClick={() => setRevealed((r) => ({ ...r, [item.id]: !r[item.id] }))}>
                {revealed[item.id] ? "Hide Answer" : "Reveal Answer"}
              </button>
            </div>
            {hints[item.id] && item.hint && <div className="tcr-hint-text">💡 {item.hint}</div>}
            {revealed[item.id] && <div className="tcr-answer-text">✅ {item.answer}</div>}
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Quiz Block ────────────────────────────────────────────────────────────────
const QuizBlock = ({ data }) => {
  const [mode, setMode] = useState("preview");
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const startedAt = useRef(null);
  const timerRef = useRef(null);

  // Build option id→text map for result display
  const optionMap = {};
  data.questions?.forEach((q) => q.options?.forEach((o) => { optionMap[o.id] = o.optionText; }));

  const startQuiz = () => {
    setAnswers({});
    setResult(null);
    startedAt.current = new Date().toISOString();
    if (data.timeLimitMinutes) setTimeLeft(data.timeLimitMinutes * 60);
    setMode("taking");
  };

  useEffect(() => {
    if (mode === "taking" && timeLeft !== null) {
      timerRef.current = setInterval(() => {
        setTimeLeft((t) => {
          if (t <= 1) { clearInterval(timerRef.current); handleSubmit(); return 0; }
          return t - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [mode]); // eslint-disable-line

  const setAnswer = (questionId, selectedOptionId, textAnswer) =>
    setAnswers((a) => ({ ...a, [questionId]: { questionId, selectedOptionId: selectedOptionId ?? null, textAnswer: textAnswer ?? null } }));

  const handleSubmit = async () => {
    clearInterval(timerRef.current);
    setSubmitting(true);
    const payload = {
      quizId: data.id,
      startedAt: startedAt.current,
      answers: data.questions.map((q) => answers[q.id] || { questionId: q.id, selectedOptionId: null, textAnswer: null }),
    };
    const res = await submitQuiz(payload);
    setSubmitting(false);
    if (res.status === 200 || res.status === 201) { setResult(res.data); setMode("results"); }
    else alert(res.message || "Submit failed");
  };

  const loadHistory = async () => {
    const res = await fetchQuizAttempts(data.id);
    if (res.status === 200) setAttempts(Array.isArray(res.data) ? res.data : []);
    setMode("history");
  };

  const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div className="tcr-quiz-card">
      {/* Preview */}
      {mode === "preview" && (
        <>
          <div className="tcr-quiz-header">
            <div>
              <div className="tcr-quiz-title">🧠 {data.title}</div>
              <div className="tcr-quiz-meta">
                <span className="tcr-badge tcr-badge-info">Pass: {data.passScore}%</span>
                {data.timeLimitMinutes && <span className="tcr-badge tcr-badge-warn">⏱ {data.timeLimitMinutes} min</span>}
                {data.totalAttempts > 0 && <span className="tcr-badge tcr-badge-info">{data.totalAttempts} attempt{data.totalAttempts > 1 ? "s" : ""}</span>}
                {data.totalAttempts > 0 && (
                  <span className={`tcr-badge ${data.passed ? "tcr-badge-pass" : "tcr-badge-fail"}`}>
                    Best: {data.bestScore}% {data.passed ? "✓ Passed" : "✗ Failed"}
                  </span>
                )}
              </div>
            </div>
            <button className="tcr-start-btn" onClick={startQuiz}>
              {data.totalAttempts > 0 ? "Retake Quiz" : "Start Quiz"}
            </button>
          </div>
          {data.totalAttempts > 0 && (
            <button className="tcr-history-btn" onClick={loadHistory}>View Attempt History</button>
          )}
        </>
      )}

      {/* Taking */}
      {mode === "taking" && (
        <div className="tcr-quiz-mode">
          <div className="tcr-quiz-title">🧠 {data.title}</div>
          {timeLeft !== null && <div className="tcr-timer">⏱ {fmtTime(timeLeft)}</div>}
          {data.questions?.map((q, qi) => (
            <div key={q.id} className="tcr-question-block">
              <div className="tcr-question-text">{qi + 1}. {q.question}</div>
              {q.questionType === "MCQ" && (
                <div className="tcr-options">
                  {q.options?.map((opt) => (
                    <div key={opt.id}
                      className={`tcr-option${answers[q.id]?.selectedOptionId === opt.id ? " selected" : ""}`}
                      onClick={() => setAnswer(q.id, opt.id, null)}>
                      <input type="radio" readOnly checked={answers[q.id]?.selectedOptionId === opt.id} />
                      {opt.optionText}
                    </div>
                  ))}
                </div>
              )}
              {q.questionType === "TRUE_FALSE" && (
                <div className="tcr-tf-btns">
                  {q.options?.map((opt) => (
                    <button key={opt.id}
                      className={`tcr-tf-btn${answers[q.id]?.selectedOptionId === opt.id ? " selected" : ""}`}
                      onClick={() => setAnswer(q.id, opt.id, null)}>
                      {opt.optionText}
                    </button>
                  ))}
                </div>
              )}
              {q.questionType === "SHORT_ANSWER" && (
                <input className="tcr-short-input" placeholder="Your answer…"
                  value={answers[q.id]?.textAnswer || ""}
                  onChange={(e) => setAnswer(q.id, null, e.target.value)} />
              )}
            </div>
          ))}
          <button className="tcr-submit-btn" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Submitting…" : "Submit Quiz"}
          </button>
        </div>
      )}

      {/* Results */}
      {mode === "results" && result && (
        <div className="tcr-results">
          <div className={`tcr-score-banner ${result.passed ? "passed" : "failed"}`}>
            <div className="tcr-score-num">{result.score}%</div>
            <div className="tcr-score-label">{result.passed ? "🎉 Passed!" : "😔 Not passed"} (Pass: {result.passScore}%)</div>
          </div>
          {result.results?.map((r) => (
            <div key={r.questionId} className={`tcr-result-item ${r.isCorrect ? "correct" : "wrong"}`}>
              <div className="tcr-result-q">{r.isCorrect ? "✅" : "❌"} {r.question}</div>
              {r.selectedOptionId && (
                <div className="tcr-result-detail">
                  Your answer: <strong>{optionMap[r.selectedOptionId] || `Option #${r.selectedOptionId}`}</strong>
                </div>
              )}
              {r.textAnswer && <div className="tcr-result-detail">Your answer: <strong>{r.textAnswer}</strong></div>}
              {!r.isCorrect && r.correctOptionId && (
                <div className="tcr-result-detail" style={{ color: "#2e7d32" }}>
                  Correct: <strong>{optionMap[r.correctOptionId] || `Option #${r.correctOptionId}`}</strong>
                </div>
              )}
              {r.explanation && <div className="tcr-result-explanation">💡 {r.explanation}</div>}
            </div>
          ))}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="tcr-retry-btn" onClick={startQuiz}>🔄 Retry</button>
            <button className="tcr-history-btn" onClick={loadHistory}>View History</button>
            <button className="tcr-history-btn" onClick={() => setMode("preview")}>← Back</button>
          </div>
        </div>
      )}

      {/* History */}
      {mode === "history" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <strong>Attempt History — {data.title}</strong>
            <button className="tcr-history-btn" onClick={() => setMode("preview")}>← Back</button>
          </div>
          {attempts.length === 0 ? <p style={{ color: "#888" }}>No attempts yet.</p> : (
            <div className="tcr-attempts-list">
              {attempts.map((a) => (
                <div key={a.attemptId} className="tcr-attempt-row">
                  <span>Attempt #{a.attemptNumber}</span>
                  <span className={`tcr-badge ${a.passed ? "tcr-badge-pass" : "tcr-badge-fail"}`}>{a.score}% {a.passed ? "Passed" : "Failed"}</span>
                  <span style={{ color: "#888" }}>{a.completedAt ? new Date(a.completedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : ""}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main Renderer ─────────────────────────────────────────────────────────────
const TopicContentRenderer = ({ topicType, topicId }) => {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!topicId) return;
    const load = async () => {
      setLoading(true);
      const res = await fetchTopicContent(topicType, topicId);
      if (res.status === 200) setBlocks(Array.isArray(res.data) ? res.data : []);
      setLoading(false);
    };
    load();
  }, [topicType, topicId]);

  if (loading) return <p style={{ color: "#888", fontSize: 14 }}>Loading content…</p>;
  if (blocks.length === 0) return null;

  return (
    <div className="tcr-wrap">
      {blocks.map((block) => {
        if (block.refType === "CONTENT" && block.content) return <ContentBlock key={block.linkId} data={block.content} />;
        if (block.refType === "PRACTICE" && block.practice) return <PracticeBlock key={block.linkId} data={block.practice} />;
        if (block.refType === "QUIZ" && block.quiz) return <QuizBlock key={block.linkId} data={block.quiz} />;
        return null;
      })}
    </div>
  );
};

export default TopicContentRenderer;
